import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import styles from './index.module.less';
import { createRef, useEffect } from 'react';
import type {
  Theme,
  SupportedHtmlTags,
  BenefitItem,
  Tools,
} from '../../../core/types';
import { Drawer, type DetailComponentProps } from '../../styleless/drawer';
import useHandlerESC from '../../../utilities/useHandlerEsc';
import { Color, Size, Align, FontType } from '../../../core/enums';
import withEventTracking from '../../../utilities/withEventTracking';
import { Box } from '../../archived/box';
import { Icon } from '../../atoms/icon';
import { useBreakpoint } from '../../styleless/breakpoint';
import { DismissibleContent } from '../../styleless/dismissible-content';
import { FocusTrap } from '../../styleless/focus-trap';
import Element from '../../../core/Element';

type ShadowType = 'light' | 'lighter';

interface Customization {
  benefit?: {
    $shadow?: ShadowType;
  };
  imageTheme?: Theme;
  titleTheme?: Theme;
  labelTheme?: Theme;
  mobileDescriptionTextTheme?: Theme;
  labelHtmlTag?: keyof SupportedHtmlTags;
  titleHtmlTag?: keyof SupportedHtmlTags;
  backBtnHtmlTag?: keyof SupportedHtmlTags;
}

interface BenefitMenuProps {
  items: BenefitItem[];
  itemTag?: keyof SupportedHtmlTags;
  labelHtmlTag?: keyof SupportedHtmlTags;
  onSelect?: (a: string) => void;
  qeId?: string;
  theme?: Theme;
  forwardedRef: React.Ref<HTMLElement>;
}

interface BackButtonProps {
  children: React.ReactNode;
  onClick: () => void;
  tools: Tools;
  qeId?: string;
  role?: string;
  ariaLabel?: string;
}

export const BackButton = withEventTracking<BackButtonProps>(
  ({ children, onClick, qeId, ariaLabel }: BackButtonProps) => (
    <Element
      qeId={qeId ?? 'BackButton'}
      Component="button"
      type="button"
      className={styles.backButton}
      onClick={onClick}
      theme={{
        paddingRight: 20,
        paddingLeft: 20,
        paddingTop: 20,
        color: Color.DeepBlue,
        bgColor: Color.Gray01,
        paddingBottom: 20,
      }}
      aria-label={ariaLabel}
    >
      {children}
    </Element>
  )
);

interface BenefitProps {
  item: BenefitItem;
  handleBackSelection: () => void;
  ref?: React.Ref<HTMLDivElement>;
  qeId?: string;
  customization?: Customization;
  children?: React.ReactNode;
  accessibilityCompliance?: boolean;
}

export const Benefit = ({
  item,
  handleBackSelection,
  qeId,
  customization = {},
  children,
  accessibilityCompliance,
}: BenefitProps) => {
  const {
    image,
    trackingDetails,
    description,
    backBtnAriaLabel,
    ariaLabelledBy,
  } = item;
  const {
    benefit,
    imageTheme,
    titleTheme,
    labelTheme,
    titleHtmlTag,
    backBtnHtmlTag,
    mobileDescriptionTextTheme,
  } = customization;
  const { current } = useBreakpoint();

  const ref = createRef<HTMLDivElement>();
  useEffect(() => {
    if (ref.current) {
      disableBodyScroll(ref.current);
      ref.current.focus();
    }
    return clearAllBodyScrollLocks;
  }, [ref]);

  useHandlerESC(handleBackSelection, true);
  const baseComp = (
    <Element className={styles.fullWidth} theme={{ bgColor: Color.White }}>
      <BackButton
        onClick={handleBackSelection}
        tools={{
          tracking: {
            methods: ['click'],
            element: item.trackingElement
              ? `${item.trackingElement}:AllBenefits`
              : 'AllBenefits',
            details: [trackingDetails ? trackingDetails : item.label, 'Close'],
          },
        }}
        ariaLabel={backBtnAriaLabel}
      >
        <Icon
          directory="us-consumer"
          imageName="axp-icon-left"
          theme={{
            color: Color.DeepBlue,
            paddingRight: 15,
          }}
          $size={Size.SM}
        />
        {!item.hideIconInBenefitDrawer && item.icon && (
          <Icon {...item.icon} theme={{ color: Color.DeepBlue }} />
        )}

        <Element
          Component={backBtnHtmlTag}
          id={ariaLabelledBy}
          theme={{
            textBody: 4,
            color: Color.DeepBlue,
            paddingLeft: 10,
            ...labelTheme,
          }}
        >
          {item.backButtonLabel || item.label}
        </Element>
      </BackButton>

      <Element className={styles.benefits} forwardedRef={ref}>
        {image && (
          <Element
            Component="img"
            src={
              typeof image === 'object'
                ? image[current] ?? image.default
                : image
            }
            alt={item.label}
            theme={{ ...imageTheme }}
          />
        )}
        {description && (
          <Element
            className="mobileDescription"
            theme={{ ...mobileDescriptionTextTheme }}
          >
            {item.description}
          </Element>
        )}

        <Element theme={{ paddingLeft: 15, paddingRight: 15 }}>
          {item.body.map(({ title, text }, i) => (
            <Box
              key={`${i}-${title}`}
              $border
              theme={{
                paddingTop: 30,
                paddingBottom: 30,
                paddingLeft: 20,
                paddingRight: 20,
                borderColor: Color.Gray02,
                marginTop: 20,
                marginBottom: 20,
              }}
              {...benefit}
            >
              <Element
                Component={titleHtmlTag}
                theme={{
                  textHeading: 4,
                  margin: 'none',
                  padding: 'none',
                  color: Color.DeepBlue,
                  ...titleTheme,
                }}
              >
                {title}
              </Element>
              <Element
                theme={{
                  padding: 'none',
                  margin: 'none',
                  paddingTop: 10,
                  textBody: 3,
                }}
              >
                {text}
              </Element>
            </Box>
          ))}
        </Element>
      </Element>
      {children}
    </Element>
  );
  return (
    <Element
      qeId={qeId}
      role="dialog"
      aria-modal="true"
      aria-labelledby={ariaLabelledBy}
    >
      {accessibilityCompliance ? (
        <FocusTrap useAriaModal={true} ariaLabel="">
          <DismissibleContent
            isOpen={true}
            toggle={handleBackSelection}
            disableClickOutside={true}
            disableKeyPress={true}
          >
            {baseComp}
          </DismissibleContent>
        </FocusTrap>
      ) : (
        baseComp
      )}
    </Element>
  );
};

export const BenefitMenu = ({
  items,
  onSelect = () => {},
  qeId,
  theme,
  forwardedRef,
  itemTag,
  labelHtmlTag,
}: BenefitMenuProps) => {
  const isListType = itemTag === 'li';
  const ComponentWrapper = isListType ? 'ul' : 'div';

  return (
    <Box
      qeId={qeId}
      Component={ComponentWrapper}
      $border
      $overflow="hidden"
      theme={{
        rounded: Size.SM,
        borderColor: Color.Gray02,
        ...theme,
      }}
      forwardedRef={forwardedRef}
    >
      {items.map(
        ({ label, icon, trackingDetails, trackingElement, ariaLabel }, i) => (
          <Element
            Component={itemTag}
            key={`benefits-${i}`}
            className={styles.feature}
            theme={{
              borderColor: Color.Gray02,
              margin: 0,
            }}
          >
            <MenuButton
              onClick={() => onSelect(label)}
              tools={{
                tracking: {
                  methods: ['click'],
                  element: trackingElement
                    ? `${trackingElement}:AllBenefits`
                    : 'AllBenefits',
                  details: [trackingDetails ? trackingDetails : label, 'Open'],
                },
              }}
              ariaLabel={ariaLabel}
            >
              {icon && <Icon {...icon} theme={{ color: Color.DeepBlue }} />}
              <Element
                Component={labelHtmlTag}
                className={styles.label}
                theme={{
                  textAlign: Align.Left,
                  paddingLeft: 10,
                  paddingRight: 10,
                  textBody: 3,
                  color: Color.Gray06,
                  margin: 0,
                }}
              >
                {label}
              </Element>
              <Icon
                directory="us-consumer"
                imageName="axp-icon-right"
                theme={{
                  color: Color.BrightBlue,
                  paddingRight: 15,
                }}
                $size={Size.SM}
              />
            </MenuButton>
          </Element>
        )
      )}
    </Box>
  );
};

interface MenuButtonProps {
  children: React.ReactNode;
  onClick: () => void;
  tools: Tools;
  ariaLabel?: string;
}

const MenuButton = withEventTracking<MenuButtonProps>(
  ({ children, onClick, ariaLabel }: MenuButtonProps) => (
    <Element
      qeId="MenuButton"
      Component="button"
      type="button"
      className={styles.button}
      onClick={onClick}
      theme={{
        paddingTop: 15,
        paddingBottom: 15,
        paddingLeft: 10,
        paddingRight: 10,
        bgColor: Color.White,
      }}
      aria-label={ariaLabel}
    >
      {children}
    </Element>
  )
);

interface Customization {
  benefit?: {
    $shadow?: ShadowType;
  };
  labelTheme?: Theme;
  labelHtmlTag?: keyof SupportedHtmlTags;
  titleTheme?: Theme;
  titleHtmlTag?: keyof SupportedHtmlTags;
  backBtnHtmlTag?: keyof SupportedHtmlTags;
  imageTheme?: Theme;
  mobileDescriptionTextTheme?: Theme;
}

export interface ListWithDrawerProps {
  data: {
    items: BenefitItem[];
  };
  qeId?: string;
  customization?: Customization;
  children?: React.ReactNode;
  accessibilityCompliance?: boolean;
  itemTag?: keyof SupportedHtmlTags;
  ariaLabel?: string;
}

const defaultCustomizations: Customization = {
  benefit: {},
  labelTheme: {},
  labelHtmlTag: 'span',
  titleHtmlTag: 'div',
  titleTheme: { fontType: FontType.BentonBold },
  imageTheme: { horizontallyFluid: true },
  backBtnHtmlTag: 'span',
  mobileDescriptionTextTheme: {
    textAlign: Align.Left,
    paddingLeft: 15,
    paddingRight: 15,
    marginTop: 15,
    marginBottom: 15,
    fontType: FontType.BentonBold,
    textBody: 3,
  },
};

export const ListWithDrawer = ({
  data: { items },
  children,
  accessibilityCompliance = true,
  qeId = 'ListWithDrawer',
  customization = {},
  itemTag = 'li',
}: ListWithDrawerProps) => {
  const customizations = { ...defaultCustomizations, ...customization };
  const itemsRef = createRef<HTMLElement>();

  return (
    <div data-qe-id={qeId}>
      <Drawer
        Menu={({ onSelect }) => (
          <BenefitMenu
            items={items}
            onSelect={onSelect}
            forwardedRef={itemsRef}
            itemTag={itemTag}
            labelHtmlTag={customizations.labelHtmlTag}
          />
        )}
        detailItems={items.reduce(
          (
            result: {
              [index: string]: React.FunctionComponent<DetailComponentProps>;
            },
            item: BenefitItem,
            i
          ) => {
            result[item.label] = ({ handleBackSelection }) => {
              const onBackSelection = () => {
                (
                  itemsRef.current?.children?.[i]
                    .firstElementChild as HTMLElement | null
                )?.focus();
                handleBackSelection();
              };
              return (
                <Benefit
                  handleBackSelection={onBackSelection}
                  item={item}
                  customization={customizations}
                  accessibilityCompliance={accessibilityCompliance}
                >
                  {children}
                </Benefit>
              );
            };
            return result;
          },
          {}
        )}
      />
    </div>
  );
};
